
import React, { useState } from 'react';
import { CreditCard, CheckCircle2, Shield, Lock, Globe, Wallet, X, Sparkles, Building2, Loader2 } from 'lucide-react';
import { COUNTRIES_CURRENCIES, BASE_PRICING } from '../constants';
import { PaymentService } from '../services/paymentService';

interface BillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (amount: number, currency: string) => void;
  role: 'business' | 'individual';
  initialCountry?: string;
  pricing?: { individual: number, business: number };
}

const BillingModal: React.FC<BillingModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  role, 
  initialCountry,
  pricing = { individual: BASE_PRICING.INDIVIDUAL, business: BASE_PRICING.BUSINESS } 
}) => {
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || 'Saudi Arabia');
  const [status, setStatus] = useState<'idle' | 'processing' | 'redirecting' | 'success'>('idle');

  if (!isOpen) return null;

  const countryData = COUNTRIES_CURRENCIES.find(c => c.country === selectedCountry) || COUNTRIES_CURRENCIES.find(c => c.country === 'United States')!;
  
  const baseUsdPrice = role === 'business' ? pricing.business : pricing.individual;
  const localPrice = (baseUsdPrice * countryData.rate).toFixed(2);

  const handlePayment = async () => {
    setStatus('processing');
    try {
      const result = await PaymentService.startPayment({
        amount: Number(localPrice),
        currency: countryData.currency as 'SAR' | 'PKR' | 'USD',
        customer: {
          name: "User",
          email: "user@example.com", // In real app, pass from current user state
          phone: "966500000000"
        },
        bookingId: "billing_subscription"
      });
      
      if (result.startsWith('SIMULATED_GATEWAY')) {
        setStatus('redirecting');
        // Extract ref if it was returned (simplified for demo)
        const ref = "MOCK_" + Date.now();
        
        // Simulate external gateway processing time
        setTimeout(async () => {
          await PaymentService.simulateWebhookSuccess(ref);
          setStatus('success');
          setTimeout(() => {
            onSuccess(Number(localPrice), countryData.currency);
          }, 1500);
        }, 2000);
      } else {
        // Real production redirect
        window.location.href = result;
      }
    } catch (err: any) {
      alert(err.message);
      setStatus('idle');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] p-4 backdrop-blur-xl animate-fadeIn">
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col relative border border-white/20">
        {status === 'idle' && (
          <button onClick={onClose} className="absolute top-6 right-6 p-2.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-20">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}

        {status === 'idle' ? (
          <>
            <div className="bg-gradient-to-br from-wedding-600 via-wedding-700 to-indigo-900 p-12 text-white relative">
              <div className="absolute bottom-0 right-0 p-8 opacity-10">
                 <Building2 className="w-32 h-32" />
              </div>
              <div className="relative z-10">
                 <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full mb-6 border border-white/10">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Premium Node Access</span>
                 </div>
                 <h2 className="text-4xl font-serif font-bold mb-4 leading-tight">Elevate Your Planning Experience</h2>
                 
                 <div className="mt-10 flex items-baseline gap-3">
                    <span className="text-gray-300 text-xl font-medium tracking-widest">{countryData.symbol}</span>
                    <span className="text-7xl font-black tracking-tighter">{localPrice}</span>
                    <span className="text-lg font-bold text-white/60">/ {role === 'business' ? 'Month' : 'Event'}</span>
                 </div>
                 <p className="mt-4 text-xs font-bold text-wedding-200 uppercase tracking-[0.2em]">Billed via local gateway in {countryData.currency}</p>
              </div>
            </div>

            <div className="p-12 space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Billing Jurisdiction</label>
                  <div className="relative group">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-wedding-500 group-focus-within:animate-spin" />
                    <select 
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-wedding-500/10 outline-none appearance-none cursor-pointer font-bold text-sm"
                    >
                      {COUNTRIES_CURRENCIES.map(c => (
                        <option key={c.country} value={c.country}>{c.flag} {c.country}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col justify-end space-y-3">
                   {[
                     'AI-Driven Decision Engine',
                     'Secure Local Payments',
                     'Instant Financial Analytics'
                   ].map(f => (
                     <div key={f} className="flex items-center text-xs font-bold text-gray-600 uppercase tracking-tight">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                        {f}
                     </div>
                   ))}
                </div>
              </div>

              <div className="pt-8 border-t border-gray-50">
                <button 
                  onClick={handlePayment}
                  className="w-full py-6 bg-gray-900 text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-lg hover:bg-black transition-all flex items-center justify-center shadow-2xl transform active:scale-[0.98]"
                >
                  <CreditCard className="w-6 h-6 mr-3" />
                  Pay with {selectedCountry === 'Saudi Arabia' ? 'SitesWay' : 'Paymob'}
                </button>
                <div className="mt-8 flex items-center justify-center gap-6 text-[9px] text-gray-400 font-black uppercase tracking-widest">
                  <div className="flex items-center"><Lock className="w-3.5 h-3.5 mr-2 text-emerald-500" /> AES-256 Encrypted</div>
                  <div className="flex items-center"><Shield className="w-3.5 h-3.5 mr-2 text-emerald-500" /> Virtual Escrow Active</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-20 flex flex-col items-center justify-center text-center space-y-8 animate-fadeIn">
            {status === 'processing' || status === 'redirecting' ? (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-wedding-100 rounded-full animate-ping opacity-25"></div>
                  <div className="relative bg-wedding-50 p-10 rounded-full">
                    <Loader2 className="w-16 h-16 text-wedding-600 animate-spin" />
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2">Connecting to Gateway</h2>
                  <p className="text-gray-500 font-medium uppercase tracking-widest text-[10px]">
                    {status === 'redirecting' ? 'Finalizing secure handshake...' : 'Securing local payment node...'}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  <span className="flex items-center"><Shield className="w-3 h-3 mr-1.5 text-emerald-500" /> Verified</span>
                  <span className="flex items-center"><Lock className="w-3 h-3 mr-1.5 text-emerald-500" /> Encrypted</span>
                </div>
              </>
            ) : (
              <>
                <div className="bg-emerald-50 p-10 rounded-full">
                  <CheckCircle2 className="w-16 h-16 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2">Payment Authorized</h2>
                  <p className="text-gray-500 font-medium uppercase tracking-widest text-[10px]">Transaction logged to virtual escrow</p>
                </div>
                <p className="text-emerald-600 font-black text-xs uppercase tracking-widest animate-pulse">Redirecting to Dashboard...</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingModal;
